import { useEffect, useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { useMetaAds } from './use-meta-ads';

/**
 * Módulo 11 — Meta Ads (`docs/ADS.md`).
 *
 * Dos cosas, y conviene no mezclarlas:
 *  - la ATRIBUCIÓN ya funciona sin configurar nada (Meta manda el anuncio en el referral del webhook);
 *  - lo que se configura aquí es la **Marketing API** (`ads_read`) para traer el **gasto**, resolver el
 *    **nombre de la campaña** (que el referral no trae) y que el CPA de los reportes se llene solo.
 *
 * El token solo se manda al guardar: el backend nunca lo devuelve (igual que las keys de IA).
 */
export function MetaAdsCard() {
  const { tenantId, canEdit } = useActiveTenantAccess();
  const { status, guardar, sincronizar, probar } = useMetaAds(tenantId);

  const [adAccountId, setAdAccountId] = useState('');
  const [token, setToken] = useState('');
  const [datasetId, setDatasetId] = useState('');

  // El estado llega del backend: se rellena el campo una sola vez (sin pisar lo que el usuario escriba).
  useEffect(() => {
    const guardado = status.data?.adAccountId ?? '';
    setAdAccountId((actual) => actual || guardado);
  }, [status.data?.adAccountId]);

  useEffect(() => {
    const guardado = status.data?.datasetId ?? '';
    setDatasetId((actual) => actual || guardado);
  }, [status.data?.datasetId]);

  const errorGuardar = guardar.isError ? getErrorMessage(guardar.error) : null;
  const errorSync = sincronizar.isError ? getErrorMessage(sincronizar.error) : null;
  const resultado = sincronizar.data ?? null;

  const errorProbar = probar.isError ? getErrorMessage(probar.error) : null;
  const resultadoProbar = probar.data ?? null;

  /**
   * Lo que se manda a «Probar credenciales»: lo escrito en el formulario (para poder diagnosticar
   * ANTES de guardar) y, si los campos están vacíos, lo que ya está guardado (lo resuelve el backend).
   */
  const credencialesDelFormulario = {
    ...(adAccountId.trim() ? { adAccountId: adAccountId.trim() } : {}),
    ...(token.trim() ? { token: token.trim() } : {}),
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meta Ads (gasto y campañas)</CardTitle>
        <CardDescription>
          De qué anuncio y de qué campaña viene cada conversación, y el gasto que alimenta el CPA de los
          reportes. Necesita un token de <strong>System User</strong> con <code>ads_read</code>. Si Meta
          contesta «(#200) … has NOT grant ads_management or ads_read», pulsa{' '}
          <strong>Probar credenciales</strong>: dice si falta el permiso en el token o la autorización de
          la cuenta publicitaria, y qué cuentas ve el token.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field
          label="Cuenta publicitaria"
          htmlFor="metaAdsAccountId"
          hint="Se acepta `1234567890` o `act_1234567890`. La ves en Ads Manager → Cuentas publicitarias."
          error={undefined}
        >
          <Input
            id="metaAdsAccountId"
            name="metaAdAccountId"
            placeholder="act_1234567890"
            disabled={!canEdit || guardar.isPending}
            value={adAccountId}
            onChange={(event) => setAdAccountId(event.target.value)}
          />
        </Field>

        <Field
          label="Token de System User"
          htmlFor="metaAdsToken"
          hint="Business Settings → Usuarios → Usuarios del sistema, con la cuenta publicitaria asignada y los permisos `ads_read` y `business_management`."
          error={undefined}
        >
          <Input
            id="metaAdsToken"
            name="metaAdsToken"
            type="password"
            autoComplete="off"
            placeholder={status.data?.tokenConfigurado ? '•••••• (ya configurado: pega otro para cambiarlo)' : 'Pega el token de larga duración'}
            disabled={!canEdit || guardar.isPending}
            value={token}
            onChange={(event) => setToken(event.target.value)}
          />
        </Field>

        <Field
          label="Dataset de la Conversions API (opcional)"
          htmlFor="metaAdsDatasetId"
          hint="Events Manager → Orígenes de datos: el id del dataset/píxel. Con esto el sistema devuelve cada COMPRA a Meta (atribución por anuncio y optimización por compras). Sin él, el gasto y el CPA siguen funcionando."
          error={undefined}
        >
          <Input
            id="metaAdsDatasetId"
            name="metaDatasetId"
            placeholder="Ej. 1234567890123456"
            disabled={!canEdit || guardar.isPending}
            value={datasetId}
            onChange={(event) => setDatasetId(event.target.value)}
          />
        </Field>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={!canEdit || guardar.isPending || (!adAccountId.trim() && !token.trim() && !datasetId.trim())}
            loading={guardar.isPending}
            onClick={() =>
              guardar.mutate({
                ...(adAccountId.trim() ? { adAccountId: adAccountId.trim() } : {}),
                ...(token.trim() ? { token: token.trim() } : {}),
                ...(datasetId.trim() ? { datasetId: datasetId.trim() } : {}),
              })
            }
          >
            Guardar
          </Button>
          <Button
            variant="secondary"
            disabled={!canEdit || !status.data?.tokenConfigurado || sincronizar.isPending}
            loading={sincronizar.isPending}
            onClick={() => sincronizar.mutate(30)}
          >
            Sincronizar ahora (30 días)
          </Button>
          <Button
            variant="secondary"
            disabled={
              !canEdit || probar.isPending || (!token.trim() && !status.data?.tokenConfigurado)
            }
            loading={probar.isPending}
            onClick={() => probar.mutate(credencialesDelFormulario)}
          >
            Probar credenciales
          </Button>
          {!canEdit ? (
            <span className="text-caption text-ink-muted">Solo un ADMIN puede configurar esto.</span>
          ) : null}
        </div>

        {errorGuardar ? <Alert tone="warning">{errorGuardar}</Alert> : null}
        {errorSync ? <Alert tone="warning">{errorSync}</Alert> : null}
        {errorProbar ? <Alert tone="warning">{errorProbar}</Alert> : null}

        {resultado ? (
          <Alert tone={resultado.ok ? 'success' : 'warning'}>{resultado.mensaje}</Alert>
        ) : null}

        {resultadoProbar ? (
          <Alert tone={resultadoProbar.ok ? 'success' : 'warning'}>{resultadoProbar.mensaje}</Alert>
        ) : null}

        {resultadoProbar?.permisos.length ? (
          <p className="text-caption text-ink-muted">
            Permisos del token
            {resultadoProbar.identidad ? ` de ${resultadoProbar.identidad}` : ''}:{' '}
            {resultadoProbar.permisos.join(', ')}
            {resultadoProbar.cuentas.length
              ? ` · Cuentas publicitarias visibles: ${resultadoProbar.cuentas
                  .map((cuenta) => cuenta.id)
                  .join(', ')}`
              : ''}
          </p>
        ) : null}

        {status.data?.syncError ? (
          <Alert tone="warning">Última sincronización con error: {status.data.syncError}</Alert>
        ) : null}

        <dl className="grid gap-2 text-body sm:grid-cols-3">
          <div>
            <dt className="text-caption text-ink-muted">Última sincronización</dt>
            <dd className="text-ink">
              {status.data?.syncedAt
                ? new Date(status.data.syncedAt).toLocaleString('es-CO')
                : 'Nunca (el gasto sigue a mano)'}
            </dd>
          </div>
          <div>
            <dt className="text-caption text-ink-muted">Anuncios con gasto</dt>
            <dd className="text-ink">{status.data?.anunciosConocidos ?? 0}</dd>
          </div>
          <div>
            <dt className="text-caption text-ink-muted">Filas de campaña sin desglose</dt>
            <dd className="text-ink">{status.data?.campanasConocidas ?? 0}</dd>
          </div>
          <div>
            <dt className="text-caption text-ink-muted">Compras a Meta (Conversions API)</dt>
            <dd className="text-ink">
              {status.data?.datasetId
                ? status.data.capiConfigurado
                  ? 'Activa: Contact + Lead + Purchase se devuelven a Meta'
                  : 'Falta el token para activarla'
                : 'Sin configurar (solo atribución)'}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}
